import { Structure, StructureJob, StructureNode, childrenOf, isActiveRow, shownPath } from '../sites/structure'

// Which rows a picking job may take, read the way planMembership does
// (routes/picking-jobs.js): an active final row of exactly the job's variety
// in the job's Site. Unassigned rows join; a row in another job with no
// picking recorded may be reassigned, but only when asked for; a row with
// picking recorded is permanent where it is and is not offered. The server
// decides again at save and names every refusal.

// One member row as GET /api/picking-jobs/:id returns it (jobMembers).
export interface Member {
  id: string
  name: string
  path: string
  exact_variety: string | null
  plant_count: number | null
  location_id: string | null
  archived_at: string | null
  picking_recorded: boolean
  picked_kg: number
}

// One refused row from a 409 MEMBERSHIP_REFUSED (membershipRefused).
export interface Refusal {
  id: string
  name: string | null
  code: string
  reason: string
}

export interface Candidate {
  row: StructureNode
  path: string
  // The job it would leave, when adding it is a reassignment.
  from: StructureJob | null
}

// jobId is null for a job not yet created: only unassigned rows are offered
// ("Only active, unassigned rows of that exact variety are offered", §7g).
export function candidates(s: Structure | null, variety: string, jobId: string | null, includeAssigned = false): Candidate[] {
  const v = variety.trim()
  if (!s || !v) return []
  const byId = new Map(s.nodes.map((n) => [n.id, n]))
  const jobs = new Map(s.jobs.map((j) => [j.id, j]))
  const hideL2 = s.site?.show_level2 === false
  const out: Candidate[] = []
  for (const n of s.nodes) {
    if (!isActiveRow(n) || n.exact_variety !== v) continue
    if (n.picking_job_id && n.picking_job_id === jobId) continue
    if (n.picking_job_id && (!jobId || !includeAssigned || n.picking_recorded)) continue
    out.push({ row: n, path: shownPath(byId, n, hideL2), from: n.picking_job_id ? jobs.get(n.picking_job_id) || null : null })
  }
  return out
}

// Level 1, then level 2, then its candidate rows, in the Structure view's
// order. A branch with no candidate is left out. With level 2 hidden the
// rows sit straight under level 1 (in level 2's order, then their own), the
// way the Structure navigator shows them.
export interface Branch {
  node: StructureNode
  path: string
  rows: Candidate[]
  children: Branch[]
}

export function candidateTree(nodes: StructureNode[], cands: Candidate[], hideL2 = false): Branch[] {
  const byParent = new Map<string, Candidate[]>()
  for (const c of cands) {
    const k = c.row.parent_id || ''
    byParent.set(k, [...(byParent.get(k) || []), c])
  }
  const order = (p: string | null) => childrenOf(nodes, p)
  const out: Branch[] = []
  for (const l1 of order(null)) {
    const children: Branch[] = []
    for (const l2 of order(l1.id)) {
      const mine = byParent.get(l2.id)
      if (!mine) continue
      const rank = new Map(order(l2.id).map((r, i) => [r.id, i]))
      const rows = [...mine].sort((a, b) => (rank.get(a.row.id) ?? 0) - (rank.get(b.row.id) ?? 0))
      children.push({ node: l2, path: `${l1.name} / ${l2.name}`, rows, children: [] })
    }
    if (!children.length) continue
    const rows = children.flatMap((c) => c.rows)
    out.push(hideL2 ? { node: l1, path: l1.name, rows, children: [] } : { node: l1, path: l1.name, rows, children })
  }
  return out
}

export type Tick = 'all' | 'some' | 'none'

export function tickOf(ids: string[], selected: Set<string>): Tick {
  const n = ids.filter((id) => selected.has(id)).length
  return n === 0 ? 'none' : n === ids.length ? 'all' : 'some'
}

// Ticking a branch selects the rows eligible now; unticking a fully ticked
// branch clears them. Rows added to the branch later do not join (§7g).
export function tickBranch(ids: string[], selected: Set<string>): Set<string> {
  const next = new Set(selected)
  if (tickOf(ids, selected) === 'all') ids.forEach((id) => next.delete(id))
  else ids.forEach((id) => next.add(id))
  return next
}

// "Create job from selection" prefills one compatible unassigned selection;
// mixed varieties must be resolved first (§7g). The reason it cannot, or ''.
export function fromSelectionBlock(rows: StructureNode[]): string {
  if (!rows.length) return 'Select rows first'
  if (rows.some((r) => r.picking_job_id)) {
    const n = rows.filter((r) => r.picking_job_id).length
    return n === 1 ? '1 selected row is already in a job' : `${n} selected rows are already in a job`
  }
  if (rows.some((r) => !r.exact_variety)) return 'A selected row has no variety'
  if (new Set(rows.map((r) => r.exact_variety)).size > 1) return 'The selection mixes varieties: resolve them first'
  return ''
}

// The rows a create-from-selection link names, checked again on arrival:
// the ones still active, unassigned and of one variety, and how many were
// left out.
export function resolveSelection(s: Structure, ids: string[]): { variety: string; rowIds: string[]; dropped: number } {
  const byId = new Map(s.nodes.map((n) => [n.id, n]))
  const live = ids.map((id) => byId.get(id)).filter((n): n is StructureNode => !!n && isActiveRow(n) && !n.picking_job_id && !!n.exact_variety)
  const varieties = [...new Set(live.map((n) => n.exact_variety as string))]
  if (varieties.length !== 1) return { variety: '', rowIds: [], dropped: ids.length }
  return { variety: varieties[0], rowIds: live.map((n) => n.id), dropped: ids.length - live.length }
}
